"use client";

import { useEffect, useRef, useState } from "react";
import { createNoise3D } from "simplex-noise";

import { cn } from "@/lib/utils";

interface VortexProps {
  children?: React.ReactNode;
  className?: string;
  containerClassName?: string;
  /** Particles for a laptop-sized area; smaller screens get proportionally fewer. */
  particleCount?: number;
  baseHue?: number;
  baseSpeed?: number;
  rangeSpeed?: number;
  baseRadius?: number;
  rangeRadius?: number;
  backgroundColor?: string;
}

/** The area `particleCount` is meant for (a laptop-width hero). */
const REFERENCE_AREA = 1280 * 640;
const MIN_PARTICLES = 100;

/** How many particles to draw on a canvas of this size. */
export function scaledParticleCount(
  particleCount: number,
  width: number,
  height: number,
) {
  const scale = Math.min(1, (width * height) / REFERENCE_AREA);

  return Math.min(
    particleCount,
    Math.max(MIN_PARTICLES, Math.round(particleCount * scale)),
  );
}

/**
 * Animated particle background. It only animates while it is on screen,
 * draws a single still frame for people who prefer reduced motion, and stops
 * completely when unmounted.
 */
export const Vortex = ({
  children,
  className,
  containerClassName,
  particleCount = 700,
  baseHue = 220,
  baseSpeed = 0.0,
  rangeSpeed = 1.5,
  baseRadius = 1,
  rangeRadius = 2,
  backgroundColor = "#000000",
}: VortexProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isSafari, setIsSafari] = useState(false);

  useEffect(() => {
    setIsSafari(/^((?!chrome|android).)*safari/i.test(navigator.userAgent));
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");

    if (!canvas || !ctx) return;

    let w = 0;
    let h = 0;
    const resize = () => {
      w = canvas.width = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
    };

    resize();

    const noise3D = createNoise3D();

    class Particle {
      x = Math.random() * w;
      y = Math.random() * h;
      vx = (Math.random() - 0.5) * rangeSpeed;
      vy = (Math.random() - 0.5) * rangeSpeed;
      radius = baseRadius + Math.random() * rangeRadius;

      update(time: number) {
        this.x += this.vx + baseSpeed;
        this.y += this.vy;

        const noiseValue = noise3D(this.x * 0.01, this.y * 0.01, time);

        this.x += noiseValue * 2;
        this.y += noiseValue * 2;

        if (this.x < 0) this.x = w;
        if (this.x > w) this.x = 0;
        if (this.y < 0) this.y = h;
        if (this.y > h) this.y = 0;
      }

      draw(context: CanvasRenderingContext2D) {
        context.beginPath();
        context.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        context.fillStyle = `hsla(${baseHue}, 100%, 50%, 0.5)`;
        context.fill();
      }
    }

    const particles = Array.from(
      { length: scaledParticleCount(particleCount, w, h) },
      () => new Particle(),
    );

    const draw = () => {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, w, h);
      for (const particle of particles) particle.draw(ctx);
    };

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;
    let onScreen = true;

    const tick = () => {
      const time = Date.now() * 0.0001;

      for (const particle of particles) particle.update(time);
      draw();
      frame = requestAnimationFrame(tick);
    };
    const stop = () => {
      cancelAnimationFrame(frame);
      frame = 0;
    };
    const start = () => {
      if (frame || !onScreen || reducedMotion.matches) return;
      frame = requestAnimationFrame(tick);
    };

    // Resizing clears the canvas: redraw right away, even when paused.
    const onResize = () => {
      resize();
      draw();
    };
    const onMotionPreferenceChange = () => {
      if (reducedMotion.matches) stop();
      else start();
    };
    const observer =
      typeof IntersectionObserver === "undefined"
        ? null
        : new IntersectionObserver(([entry]) => {
            onScreen = entry.isIntersecting;
            if (onScreen) start();
            else stop();
          });

    window.addEventListener("resize", onResize);
    reducedMotion.addEventListener("change", onMotionPreferenceChange);
    observer?.observe(canvas);
    draw();
    start();

    return () => {
      stop();
      observer?.disconnect();
      reducedMotion.removeEventListener("change", onMotionPreferenceChange);
      window.removeEventListener("resize", onResize);
    };
  }, [
    particleCount,
    baseHue,
    baseSpeed,
    rangeSpeed,
    baseRadius,
    rangeRadius,
    backgroundColor,
  ]);

  return (
    <div className={cn("relative h-full w-full", containerClassName)}>
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        className="absolute inset-0 z-0 h-full w-full"
        style={{
          filter: isSafari ? "blur(0px)" : "blur(1px)",
        }}
      />
      <div className={cn("relative z-10", className)}>{children}</div>
    </div>
  );
};
